import { Box, Menu, MenuItem, Typography } from '@mui/material'
import { MouseEvent, useCallback, useRef, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import { BaseLayout, Head } from '@/components/layouts'
import {
  CommentList,
  CommentListProps,
  ReplyFormRef,
  ReplyFormValues,
} from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  artistId: string
  comments: CommentListProps['comments']
  user: CommentListProps['user']
  replyToDisplayName?: string
  itemId: string
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onDeleteComment: CommentListProps['onDeleteComment']
  onBlockUser: CommentListProps['onBlockUser']
  onReportUser: CommentListProps['onReportUser']
  onLikeOrUnlikeComment: CommentListProps['onLikeOrUnlikeComment']
  onFetchMore: CommentListProps['onFetchMore']
  onCreateItemComment: (
    values: ReplyFormValues,
    artistId: string
  ) => Promise<void>
  onReplyToReply: CommentListProps['onReplyToReply']
  onRemoveReplyToReply: () => void
}

export const ItemCommentTemplate: Template<State, Action> = ({
  state,
  onDeleteComment,
  onReportUser,
  onBlockUser,
  onLikeOrUnlikeComment,
  onFetchMore,
  onCreateItemComment,
  onReplyToReply,
  onRemoveReplyToReply,
}) => {
  const { routerNames } = useRouter()
  const { t } = useTranslation('itemComment')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedFilter, setSelectedFilter] = useState<string>('0')
  const replyFormRef = useRef<ReplyFormRef>(null)

  const handleOpenFilter = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget)
    },
    []
  )

  const handleCloseFilter = useCallback((value: string) => {
    setAnchorEl(null)
    setSelectedFilter(value)
  }, [])

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ItemComment} />
      <BaseLayout
        navigation={{
          type: 'replyForm',
          props: {
            onReply: values => onCreateItemComment(values, state.artistId),
            visibleImage: true,
            userId: state.user?.id ?? '',
            replyToDisplayName: state.replyToDisplayName,
            onRemoveReplyToReply: onRemoveReplyToReply,
            ref: replyFormRef,
          },
        }}
        header={{
          back: true,
          title: t('title'),
          /**
           * @description
           * Open when function is implemented in the future
           * https://app.clickup.com/t/908127/UT-4791
           */
          right: false && (
            <Box>
              <IconButton.Slider
                size="medium"
                edge="end"
                onClick={handleOpenFilter}
              />
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseFilter}
              >
                {filterOptions.map(option => (
                  <MenuItem
                    key={option.key}
                    selected={selectedFilter === option.value}
                    onClick={() => handleCloseFilter(option.value)}
                    sx={{ minWidth: 200 }}
                  >
                    <Typography variant="body2">{t(option.key)}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          ),
        }}
      >
        <Box sx={{ py: 2 }}>
          <CommentList
            type="items"
            typeId={state.itemId}
            hiddenHeader
            showReaction
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            comments={state.comments}
            user={state.user}
            onDeleteComment={onDeleteComment}
            onReportUser={onReportUser}
            onBlockUser={onBlockUser}
            onLikeOrUnlikeComment={onLikeOrUnlikeComment}
            onFetchMore={onFetchMore}
            onReplyToReply={onReplyToReply}
            onClickReply={() => replyFormRef.current?.focus()}
          />
        </Box>
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const filterOptions: { key: string; value: string }[] = [
  { key: 'createdOrder', value: '0' },
  { key: 'likeOrder', value: '1' },
]
